// Common scaffold for every field component: label (with required marker)
// + caller-rendered input slot + helpText + inline error + ARIA wiring.
//
// **Render-prop API.** The shell owns the wrapper `<div>` + `<label>` +
// helpText `<p>` + error `<p>` + ARIA id computation. Consumers render
// the input element (and any siblings like the ImageUrlField's CC
// library link) inside the `children` slot, receiving `{ attrs,
// inputClass }`:
//   - `attrs` spreads onto the input element (id + aria-* attrs)
//   - `inputClass` is the standard input class with error-state baked in
//
// Why render prop, not context: single consumer per shell instance, one
// child component to plumb. A context would require a hook + Provider
// boundary for one read site.
//
// **Slot ordering.** The render-prop output sits between the label and
// helpText/error so consumers can render multiple siblings inside the
// slot (input + adjacent link, etc.) without reordering helpText/error.

import type { ComponentChild } from 'preact'

export interface FieldSlot {
  readonly attrs: FieldSlotAttrs
  readonly inputClass: string
}

export interface FieldSlotAttrs {
  readonly id: string
  readonly 'aria-required': boolean
  readonly 'aria-invalid'?: true
  readonly 'aria-describedby'?: string
}

export interface FieldShellProps {
  readonly id: string
  readonly label: string
  readonly required?: boolean
  readonly helpText?: string
  readonly error?: string
  readonly children: (slot: FieldSlot) => ComponentChild
}

export function FieldShell({
  id,
  label,
  required = false,
  helpText,
  error,
  children,
}: FieldShellProps) {
  const helpId = helpText !== undefined ? `${id}-help` : undefined
  const errorId = error !== undefined ? `${id}-error` : undefined
  const describedBy =
    [helpId, errorId].filter((x): x is string => x !== undefined).join(' ') || undefined

  const attrs: FieldSlotAttrs = {
    id,
    'aria-required': required,
    ...(error !== undefined ? { 'aria-invalid': true as const } : {}),
    ...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {}),
  }
  const inputClass =
    'rounded border bg-white px-2 py-1.5 text-sm text-gray-900 ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500 ' +
    (error !== undefined ? 'border-red-300' : 'border-gray-300')

  return (
    <div class="flex flex-col gap-1">
      <label htmlFor={id} class="text-sm font-medium text-gray-800">
        {label}
        {required && (
          <span class="ml-0.5 text-red-600" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children({ attrs, inputClass })}
      {helpText !== undefined && (
        <p id={helpId} class="m-0 text-xs text-gray-600">
          {helpText}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} class="m-0 text-xs text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
